import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  OverviewContainer,
  BasicInformation,
  BasicInformationContent,
  BasicInformationHeader,
  LoginInformation,
  HighlightCardsContainer,
  HighlightCardColumn,
  InfoItemRow,
  BasicInformationLeft,
  BasicInformationRight,
  BasicInformationColumn,
  BasicInformationColumnItem,
  AvatarWrapper,
} from "./Overview.styled";
import {
  Typography,
  Icon,
  Card,
  Avatar,
  Tag,
  Button,
  HighlightCards,
} from "@ann/web-components";
import { useStore } from "../../../../../../../hooks";
import { useToast } from "../../../../../../../components/toast";
import { updateDepartment } from "../../../../../../../services/departments";
import { SubDepartmentsProvider, useUsersForDepartments } from "../../../../context";
import SubDepartments from "./components/SubDepartments";
import EditDepartmentModal from "./components/EditDepartmentModal";

const mockDepartmentData = {
  department_name: "Sales Department",
  owner_name: "Mike Abbott",
  owner_email: "mikeabbot@mail.com",
  address: "123 Market Street, Suite 500, San Francisco, CA 94103",

  status: "ACTIVE",

  profile_photo_url: "https://placehold.co/96x96/4CAF50/FFF?text=SD",
  owner_avatar: "https://placehold.co/32x32/2196F3/FFF?text=MA",

  user_count: 45,
  total_users: 58,
  total_agents: 234,
  active_workflows: 12,
  active_campaigns: 5,

  created_at: "2025-03-12T10:30:00Z",
  updated_at: "2025-12-05T14:20:00Z",

  created_by: {
    name: "Emma Wilson",
    role: "Sales Manager",
    avatar: "https://placehold.co/32x32/FF9800/FFF?text=EW",
  },
};

const formatCreationDate = (date) => {
  if (!date) return null;
  try {
    return dayjs(date).format("MMM D · YYYY");
  } catch (error) {
    console.error("Error formatting creation date:", error);
    return null;
  }
};

const Overview = () => {
  const { departmentsStore } = useStore();
  const toast = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { adminOwnerOptions, loading: loadingUsers, error: errorUsers, fetchUsers } = useUsersForDepartments();

  useEffect(() => {
    if (isEditModalOpen) {
      fetchUsers({ page_size: 50 });
    }
  }, [isEditModalOpen, fetchUsers]);

  const department = departmentsStore.currentDepartment || mockDepartmentData;
  const departmentName =
    department.department_name || department.name || "Department";
  const ownerName = department.owner_name || department.owner_name || "";
  const ownerEmail = department.owner_email || "";
  const status = department.status || "ACTIVE";
  const createdAt = department.created_at || mockDepartmentData.created_at;
  const userCount = department.user_count || mockDepartmentData.user_count;
  const totalUsers = department.total_users || mockDepartmentData.total_users;
  const totalAgents =
    department.total_agents || mockDepartmentData.total_agents;
  const activeWorkflows =
    department.active_workflows || mockDepartmentData.active_workflows;
  const activeCampaigns =
    department.active_campaigns || mockDepartmentData.active_campaigns;
  const photoUrl =
    department.profile_photo_url ||
    department.photo_url ||
    mockDepartmentData.profile_photo_url;

  return (
    <OverviewContainer>
      <BasicInformation id="department-information">
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Department Information
          </Typography>

          <Button
            type="secondary"
            label="Edit department"
            size="small"
            disableEffects="true"
            iconLeft="edit"
            onClick={() => setIsEditModalOpen(true)}
          />
        </BasicInformationHeader>

        <Card size="medium">
          <BasicInformationContent>
            <BasicInformationLeft>
              <AvatarWrapper>
                <Avatar
                  alt="Default avatar"
                  size="xl"
                  src={
                    photoUrl || "https://placehold.co/96x96/4CAF50/FFF?text=SD"
                  }
                />
              </AvatarWrapper>
            </BasicInformationLeft>
            <BasicInformationRight className="department-information-content-right">
              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Name
                  </Typography>
                  <Typography
                    category="body"
                    size="medium"
                    weight="regular"
                    color="body"
                  >
                    {departmentName}
                  </Typography>
                </BasicInformationColumnItem>

                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Status
                  </Typography>
                  <div>
                    <Tag
                      color={status === "ACTIVE" ? "success" : "neutral"}
                      onClose={() => {}}
                      size="medium"
                      text={status === "ACTIVE" ? "Active" : "Inactive"}
                    />
                  </div>
                </BasicInformationColumnItem>
              </BasicInformationColumn>

              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Owner
                  </Typography>
                  <InfoItemRow gapSpacing="8px">
                    <Avatar
                      alt={ownerName}
                      size="sm"
                      src={mockDepartmentData.owner_avatar}
                    />
                    <Typography
                      category="body"
                      size="medium"
                      weight="regular"
                      color="body"
                    >
                      {ownerName} {ownerEmail}
                    </Typography>
                  </InfoItemRow>
                </BasicInformationColumnItem>
              </BasicInformationColumn>

              <BasicInformationColumn>
                <BasicInformationColumnItem>
                  <Typography category="body" size="medium" weight="regular">
                    Created on
                  </Typography>
                  <InfoItemRow gapSpacing="8px">
                    <Icon name="calendarToday" size="small" />
                    <Typography
                      category="body"
                      size="medium"
                      weight="regular"
                      color="body"
                    >
                      {formatCreationDate(createdAt)}
                    </Typography>
                  </InfoItemRow>
                </BasicInformationColumnItem>
              </BasicInformationColumn>
            </BasicInformationRight>
          </BasicInformationContent>
        </Card>
      </BasicInformation>

      <LoginInformation>
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Department Statistics
          </Typography>
        </BasicInformationHeader>

        <HighlightCardsContainer>
          <HighlightCardColumn>
            <HighlightCards
              variant="lightBlue"
              label="Active Users"
              value={`${userCount} of ${totalUsers}`}
              icon="group"
              showIcon
            />
          </HighlightCardColumn>
          <HighlightCardColumn>
            <HighlightCards
              variant="orange"
              label="Total Agents"
              value={totalAgents.toString()}
              icon="aiChat"
              showIcon
            />
          </HighlightCardColumn>
          <HighlightCardColumn>
            <HighlightCards
              variant="green"
              label="Active Workflows"
              value={`${activeWorkflows} Running`}
              icon="bolt"
              showIcon
            />
          </HighlightCardColumn>
          <HighlightCardColumn>
            <HighlightCards
              variant="purple"
              label="Active Campaigns"
              value={`${activeCampaigns} Across Email & SMS`}
              icon="schedule"
              showIcon
            />
          </HighlightCardColumn>
        </HighlightCardsContainer>
      </LoginInformation>

      <SubDepartmentsProvider>
        <SubDepartments
          departmentName={departmentName}
          onAddSubDepartment={() => {
            console.log("Add sub-department clicked");
          }}
        />
      </SubDepartmentsProvider>

      <EditDepartmentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        adminOwnerOptions={adminOwnerOptions}
        loadingUsers={loadingUsers}
        errorUsers={errorUsers}
        onUpdateDepartment={async (departmentData) => {
          if (!departmentsStore.currentDepartment) return;

          try {
            const departmentUuid =
              departmentsStore.currentDepartment.department_uuid ||
              departmentsStore.currentDepartment.department_id ||
              departmentsStore.currentDepartment.id;

            if (!departmentUuid) {
              toast.error({
                title: "Error",
                message: "Department UUID is required",
              });
              return;
            }

            const getOwnerUuid = (value) => {
              const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
              if (uuidRegex.test(value)) {
                return value;
              }
              const hash = value.split('').reduce((acc, char) => {
                return ((acc << 5) - acc) + char.charCodeAt(0);
              }, 0);
              return `11111111-2222-3333-4444-${Math.abs(hash).toString().padStart(12, '0')}`;
            };

            const params = {
              department_uuid: departmentUuid,
            };

            if (departmentData.name !== undefined) {
              params.department_name = departmentData.name;
            }
            if (departmentData.description !== undefined) {
              params.description = departmentData.description;
            }
            if (departmentData.adminOwner?.value) {
              params.owner_uuid = getOwnerUuid(departmentData.adminOwner.value);
            }

            const response = await updateDepartment(params);

            const statusCode = response?.status_code || response?.statusCode;
            const isValidResponse = statusCode === 200 || statusCode === 201;

            if (!isValidResponse) {
              const errorMessage = response?.msg_code?.[0]?.description || 
                                   response?.msgCode?.[0]?.description || 
                                   'Failed to update department';
              toast.error({
                title: "Error",
                message: errorMessage,
              });
              return;
            }

            const updatedData = response.body || response?.body || {};

            const currentId =
              departmentsStore.currentDepartment.department_id ||
              departmentsStore.currentDepartment.id;

            const updatedDepartment = {
              ...departmentsStore.currentDepartment,
              department_name: updatedData.department_name || updatedData.departmentName || departmentData.name,
              name: updatedData.department_name || updatedData.departmentName || departmentData.name,
              description: updatedData.description !== undefined ? updatedData.description : departmentData.description,
              owner_uuid: updatedData.owner_uuid || updatedData.ownerUuid,
              owner_name: departmentData.adminOwnerName || departmentsStore.currentDepartment.owner_name,
              owner_email: departmentData.adminOwner?.value || departmentsStore.currentDepartment.owner_email,
              group_uuid: updatedData.group_uuid || updatedData.groupUuid,
              group_tenant_tree_uuid: updatedData.group_tenant_tree_uuid || updatedData.groupTenantTreeUuid,
            };

            departmentsStore.setCurrentDepartment(updatedDepartment);

            const departmentInList = departmentsStore.departmentList.find(
              (dept) => dept.department_id === currentId || dept.id === currentId || dept.department_uuid === departmentUuid
            );

            if (departmentInList) {
              Object.assign(departmentInList, {
                department_name: updatedData.department_name || updatedData.departmentName || departmentData.name,
                name: updatedData.department_name || updatedData.departmentName || departmentData.name,
                description: updatedData.description !== undefined ? updatedData.description : departmentData.description,
                owner_uuid: updatedData.owner_uuid || updatedData.ownerUuid,
                group_uuid: updatedData.group_uuid || updatedData.groupUuid,
                group_tenant_tree_uuid: updatedData.group_tenant_tree_uuid || updatedData.groupTenantTreeUuid,
              });
            }

            toast.success({
              title: "Department updated successfully",
              message: `${updatedData.department_name || updatedData.departmentName || departmentData.name} was updated successfully`,
            });

            setIsEditModalOpen(false);
          } catch (error) {
            console.error("Error updating department:", error);
            toast.error({
              title: "Error",
              message: error.message || "Failed to update department",
            });
          }
        }}
        department={department}
      />
    </OverviewContainer>
  );
};

export default Overview;
